/**
 * Agent 매칭 통계 모달 (M13 / C-3).
 *
 * 백엔드의 ``Record.csv_data['agent_id']`` 기반 집계를 가져와 테이블로 표시.
 * 별도 페이지 대신 모달로 둔 이유: 통계 보기 → 곧바로 해당 agent 편집 모달 전환
 * 흐름을 자연스럽게 잇기 위함.
 *
 * 표시:
 *   - 요약: 총 records / UNKNOWN(79999) 비율
 *   - 행: agent_id / 공식명 / 매칭 / 반려 / 첫매칭 / 마지막매칭
 *   - 행 클릭 → onSelectAgent(agent_id) (매칭된 agent 가 현재 페이지에 있으면 편집 모달)
 *
 * 필터:
 *   - "최근 30일" / "전체" 토글. 기본은 전체.
 */

import { useEffect, useMemo, useState } from "react";
import { BarChart3, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { UNKNOWN_AGENT_ID, type AgentStatsRow } from "@/lib/api/agents";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/cn";

import { useAgentStats } from "./useAgents";

type RangePreset = "all" | "30d";

function isoMinus30d(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString();
}

function useFormatDate() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "ko" ? "ko-KR" : "ja-JP";
  return (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
  };
}

interface Props {
  onClose: () => void;
  onSelectAgent?: (agentId: string) => void;
}

export default function AgentStatsModal({ onClose, onSelectAgent }: Props) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [range, setRange] = useState<RangePreset>("all");

  const params = useMemo(() => (range === "30d" ? { from: isoMinus30d() } : {}), [range]);
  const { data, isLoading, error, refetch, isFetching } = useAgentStats(params);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const apiError = error instanceof ApiError ? error.detail : (error?.message ?? null);

  const rows: AgentStatsRow[] = data?.rows ?? [];
  const total = data?.total_records ?? 0;
  const unknown = data?.unknown_count ?? 0;
  const unknownRatio = total > 0 ? (unknown / total) * 100 : 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            {t("admin.agents.stats.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-4 overflow-y-auto px-5 py-4">
          {/* Range filter */}
          <div className="flex items-center justify-between">
            <div
              role="tablist"
              aria-label={t("admin.agents.stats.rangeLabel")}
              className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs"
            >
              <button
                type="button"
                role="tab"
                aria-selected={range === "all"}
                onClick={() => setRange("all")}
                className={
                  range === "all"
                    ? "bg-blue-600 px-3 py-1.5 font-medium text-white"
                    : "bg-white px-3 py-1.5 text-slate-600 hover:bg-slate-50"
                }
              >
                {t("admin.agents.stats.rangeAll")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={range === "30d"}
                onClick={() => setRange("30d")}
                className={
                  range === "30d"
                    ? "bg-blue-600 px-3 py-1.5 font-medium text-white"
                    : "bg-white px-3 py-1.5 text-slate-600 hover:bg-slate-50"
                }
              >
                {t("admin.agents.stats.range30d")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isFetching ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              {t("common.actions.refresh")}
            </button>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <SummaryCard
              label={t("admin.agents.stats.totalRecords")}
              value={total.toLocaleString()}
            />
            <SummaryCard
              label={t("admin.agents.stats.unknownCount")}
              value={unknown.toLocaleString()}
              accent={unknown > 0 ? "warn" : undefined}
            />
            <SummaryCard
              label={t("admin.agents.stats.unknownRatio")}
              value={`${unknownRatio.toFixed(1)}%`}
              accent={unknownRatio >= 10 ? "warn" : undefined}
            />
          </div>

          {apiError ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {apiError}
            </div>
          ) : null}

          {/* Table */}
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <Th>{t("admin.agents.stats.colAgentId")}</Th>
                  <Th>{t("admin.agents.stats.colOfficialName")}</Th>
                  <Th className="text-right">{t("admin.agents.stats.colMatchCount")}</Th>
                  <Th className="text-right">{t("admin.agents.stats.colRejectedCount")}</Th>
                  <Th>{t("admin.agents.stats.colFirstMatch")}</Th>
                  <Th>{t("admin.agents.stats.colLastMatch")}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center italic text-slate-400">
                      {t("admin.agents.stats.empty")}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const isUnknown = r.agent_id === UNKNOWN_AGENT_ID;
                    return (
                      <tr key={r.agent_id} className={cn(isUnknown && "bg-amber-50/40")}>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => onSelectAgent?.(r.agent_id)}
                            disabled={!onSelectAgent || isUnknown}
                            className="font-mono font-medium text-slate-800 transition hover:text-blue-600 disabled:cursor-default disabled:hover:text-slate-800"
                            title={
                              onSelectAgent && !isUnknown
                                ? t("admin.agents.stats.openAgent", { id: r.agent_id })
                                : undefined
                            }
                          >
                            {r.agent_id}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {r.official_name ?? (
                            <span className="italic text-slate-400">
                              {t("admin.agents.stats.unknownAgentLabel")}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {r.match_count.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">
                          {r.rejected_count > 0 ? (
                            <span className="text-amber-700">{r.rejected_count}</span>
                          ) : (
                            "0"
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{formatDate(r.first_match_at)}</td>
                        <td className="px-3 py-2 text-slate-500">{formatDate(r.last_match_at)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="flex justify-end border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t("common.actions.close")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }: { label: string; value: string; accent?: "warn" }) {
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2",
        accent === "warn" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50",
      )}
    >
      <div className="text-[11px] text-slate-500">{label}</div>
      <div
        className={cn(
          "mt-0.5 font-mono text-sm font-semibold tabular-nums",
          accent === "warn" ? "text-amber-800" : "text-slate-900",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        "px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-slate-500",
        className,
      )}
    >
      {children}
    </th>
  );
}
